import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, DollarSign, Shield, Package } from 'lucide-react';

const AirCargoPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="air-cargo-page" className="bg-white">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/ac1.jpg"
            alt="Cargo aircraft"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 container-padding w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-blue-700 text-sm font-medium tracking-widest uppercase mb-6">
              Our Services
            </span>
            <h1 
              data-testid="service-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Air Cargo
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl mb-8">
              Time-critical, cost critical, oversized, hazardous, or sensitive — leave the logistical arrangements to Singapore Air Charter.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-enquiry-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
              >
                Cargo Enquiry
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:charter@singaporeaircharter.com"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-white/20 transition-all duration-300"
              >
                charter@singaporeaircharter.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-gap">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 
                className="heading-lg text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                We'll Get Your Shipment Moving
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="body-large mb-6">
                From dedicated freighters and heavy-lifters to low volume precious packages sent by private jet or belly-hold solutions on passenger aircraft, we'll get your shipment moving.
              </p>
              <p className="body-default mb-6">
                To and from Southeast Asia is our speciality, ably assisted by local knowledge and our established relationships with local air cargo carriers and airlines. Further afield, our team's representatives extend to Australia and the USA so you get 24/7 support no matter where your shipment is heading.
              </p>
              <Link
                to="/contact"
                data-testid="content-cta-btn"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-slate-800 transition-all duration-300"
              >
                Get Cargo Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="/ac2.jpg"
                alt="Aircraft cargo"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cargo Types */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">Solutions</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Cargo Solutions
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Time Critical */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-slate-100"
            >
              <div className="text-blue-700 mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Time Critical
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                If it needs to get there fast, Singapore Air Charter gets to work fast. We'll present the Express industry options available, reserve 'next flight out' capacity or secure a suitable aircraft. We then track it in real-time and ensure expedited handling at both ends. Need parts for your drilling rig in Papua New Guinea? Spare engine to Jakarta? Medical isotopes to Singapore? Whatever your urgent requirement, talk to Singapore Air Charter.
              </p>
            </motion.div>

            {/* Cost Critical */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-slate-100"
            >
              <div className="text-blue-700 mb-4">
                <DollarSign className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Cost Critical
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Not everything needs to get there yesterday. If economy works best, Singapore Air Charter explores cost-effective options including spare capacity, belly freight, consolidation of smaller shipments or simply holding off long enough to catch a more favourable rate. When sea is too slow and airfreight's the go, contact Singapore Air Charter with your enquiry.
              </p>
            </motion.div>

            {/* Super Sensitive */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-slate-100"
            >
              <div className="text-blue-700 mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Super Sensitive
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                In the realm of live primates in climate pods, lithium batteries triple-bagged, vintage Ferraris on shock-absorbers, orchids at four degrees celsius and all other types of special cargo, Singapore Air Charter provides you with a single point of contact from initial enquiry to final delivery. Allow us to secure your space on an aircraft, track your sensitive shipment and see the mission through to its completion.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-gap">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 
              className="heading-lg text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Air Cargo Enquiry?
            </h2>
            <p className="body-large mb-10 max-w-2xl mx-auto">
              Contact the Singapore Air Charter team for all cargo enquiries and let us handle your logistics.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="final-cta-btn"
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
              >
                Cargo Enquiry
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:charter@singaporeaircharter.com"
                className="inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-900 px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-slate-50 transition-all duration-300"
              >
                charter@singaporeaircharter.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AirCargoPage;
